import React from "react";
import {
  Button,
  Col,
  DatePicker,
  Flex,
  Input,
  Modal,
  Row,
  Select,
  Spin,
} from "antd";
import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import Swal from "sweetalert2";

import APIService from "../../Service/APIService.js";
import { hasPermission } from "../../Config/permissions.js";
import TitleBar from "../../common/title-bar/title-bar.js";
import TableOptionsCell from "../../common/table/table-options-cell/index.js";
import CommonTable from "../../common/table/index.js";
import TableLink from "../../common/table/table-link/index.js";
import { colors } from "../../theme/colors.js";
import { useAuth } from "../AuthProvider.js";
import DateRangeFilter from "../Orders/DateRangeFilter.js";
import {
  formatCurrency,
  getPaymentAppliedDisplayTotal,
} from "../Utils/Helpers.js";

const PAYMENT_TYPES = [
  "Check",
  "Wire",
  "ACH",
  "Credit Card",
  "Bank Transfer",
].map((value) => ({ label: value, value }));

const emptyPaymentForm = {
  invoice: null,
  checkNumber: "",
  appliedTotal: "",
  appliedDate: null,
  paymentType: null,
};

const isTruthyValue = (value) => {
  if (value === true || value === 1) return true;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "1";
  }
  return false;
};

const hasStripePaymentReference = (payment) =>
  Boolean(
    payment?.StripeCheckoutAttemptID ||
      payment?.StripeCheckoutSessionID ||
      payment?.StripePaymentIntentID ||
      String(payment?.CheckNumber ?? "").startsWith("pi_")
  );

const resolvePaymentSource = (payment) => {
  const storedSource = String(payment?.PaymentSource ?? "")
    .trim()
    .toLowerCase();
  if (storedSource) return storedSource;
  if (
    isTruthyValue(payment?.IsAutomaticallyApplied) &&
    isTruthyValue(payment?.PrepaidOrder)
  ) {
    return "direct_order_prepaid";
  }
  if (hasStripePaymentReference(payment)) return "invoice_stripe";
  return "manual";
};

const isReadOnlyPayment = (payment) =>
  isTruthyValue(payment?.PrepaidOrder) ||
  resolvePaymentSource(payment) !== "manual";

const normalizePayment = (payment) => ({
  ...payment,
  AppliedTotalRaw: Number(payment.AppliedTotal),
  AppliedDateRaw: payment.AppliedDate,
  PaymentAppliedDisplayTotal: formatCurrency(
    getPaymentAppliedDisplayTotal(payment)
  ),
  AppliedTotal: formatCurrency(payment.AppliedTotal),
  AppliedDate: dayjs(payment.AppliedDate).format("MM-DD-YYYY"),
});

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.data ||
  error?.message ||
  fallback;

const PaymentsDashboard = () => {
  const { user: userData } = useAuth();
  const [allPayments, setAllPayments] = React.useState([]);
  const [paymentsAppliedData, setPaymentsAppliedData] = React.useState([]);
  const [invoices, setInvoices] = React.useState([]);
  const [modalMode, setModalMode] = React.useState(null);
  const [selectedPayment, setSelectedPayment] = React.useState(null);
  const [formValues, setFormValues] = React.useState(emptyPaymentForm);
  const [searchKeyword, setSearchKeyword] = React.useState("");
  const [activeDateRange, setActiveDateRange] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);

  const canAddPayment = hasPermission(userData, "payment.add");
  const canEditPayment = hasPermission(userData, "payment.edit");
  const canDeletePayment = hasPermission(userData, "payment.delete");

  const applySearch = React.useCallback((payments, keyword) => {
    const term = keyword.trim().toLowerCase();
    if (!term) return payments;

    return payments.filter((payment) =>
      [
        payment.CheckNumber,
        payment.AccountName,
        payment.InvoiceNumber,
        payment.PaymentType,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }, []);

  const loadPayments = React.useCallback(
    async (dateRange = activeDateRange) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ type: "all" });
        if (dateRange?.startDate) {
          params.set("startDate", dateRange.startDate);
        }
        if (dateRange?.endDate) {
          params.set("endDate", dateRange.endDate);
        }

        const apiService = new APIService();
        const response = await apiService.getDataFromBackend(
          `getpaymentsapplied?${params.toString()}`
        );
        const payments = (response?.data?.data ?? []).map(normalizePayment);
        setAllPayments(payments);
        setPaymentsAppliedData(applySearch(payments, searchKeyword));
      } catch (error) {
        setAllPayments([]);
        setPaymentsAppliedData([]);
        await Swal.fire({
          icon: "error",
          title: "Unable to Load Payments",
          text: getErrorMessage(error, "Please try again"),
        });
      } finally {
        setLoading(false);
      }
    },
    [activeDateRange, applySearch, searchKeyword]
  );

  React.useEffect(() => {
    loadPayments(null);
    // Initial load only. Later refreshes retain the active filters explicitly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateFormValue = (field, value) => {
    setFormValues((currentValues) => ({
      ...currentValues,
      [field]: value,
    }));
  };

  const resetModal = () => {
    setModalMode(null);
    setSelectedPayment(null);
    setFormValues(emptyPaymentForm);
  };

  const loadInvoiceOptions = async () => {
    if (invoices.length > 0) return invoices;

    const apiService = new APIService();
    const response = await apiService.getDataFromBackend("getinvoicedashboard");
    const seenInvoiceNumbers = new Set();
    const options = (response?.data?.data ?? [])
      .filter((invoice) => {
        if (seenInvoiceNumbers.has(invoice.InvoiceNumber)) return false;
        seenInvoiceNumbers.add(invoice.InvoiceNumber);
        return true;
      })
      .map((invoice) => ({
        ...invoice,
        label: invoice.InvoiceNumber,
        value: invoice.ID,
      }));
    setInvoices(options);
    return options;
  };

  const openAddModal = async () => {
    if (!canAddPayment) return;

    setSubmitting(true);
    try {
      await loadInvoiceOptions();
      setSelectedPayment(null);
      setFormValues(emptyPaymentForm);
      setModalMode("add");
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Unable to Load Invoices",
        text: getErrorMessage(error, "Please try again"),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const openPaymentModal = (payment, requestedMode) => {
    const mode =
      requestedMode === "edit" &&
      canEditPayment &&
      !isReadOnlyPayment(payment)
        ? "edit"
        : "view";

    setSelectedPayment(payment);
    setFormValues({
      invoice: {
        ...payment,
        value: payment.InvoiceID,
        label: payment.InvoiceNumber,
      },
      checkNumber: payment.CheckNumber ?? "",
      appliedTotal: Number.isFinite(payment.AppliedTotalRaw)
        ? String(payment.AppliedTotalRaw)
        : "",
      appliedDate: payment.AppliedDateRaw
        ? dayjs(payment.AppliedDateRaw)
        : null,
      paymentType: payment.PaymentType ?? null,
    });
    setModalMode(mode);
  };

  const validatePaymentForm = ({ requireInvoice }) => {
    const amount = Number(formValues.appliedTotal);
    if (
      (requireInvoice && !formValues.invoice) ||
      !formValues.checkNumber.trim() ||
      !Number.isFinite(amount) ||
      amount <= 0 ||
      !formValues.appliedDate ||
      !formValues.paymentType
    ) {
      Swal.fire({
        icon: "error",
        title: "Invalid Payment",
        text: "Please fill all required fields with valid values.",
      });
      return false;
    }
    return true;
  };

  const addPayment = async () => {
    if (!canAddPayment || !validatePaymentForm({ requireInvoice: true })) {
      return;
    }

    const invoice = formValues.invoice;
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You are about to create a payment for this invoice.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: colors["successColor"],
      cancelButtonColor: colors["failureColor"],
      confirmButtonText: "Confirm",
    });
    if (!result.isConfirmed) return;

    setSubmitting(true);
    try {
      const apiService = new APIService();
      await apiService.addDataToBackend(
        {
          CheckNumber: formValues.checkNumber.trim(),
          AppliedTotal: Number(formValues.appliedTotal),
          AppliedDate: formValues.appliedDate.format("YYYY-MM-DD"),
          PaymentType: formValues.paymentType,
          InvoiceID: invoice.ID,
          InvoiceNumber: invoice.InvoiceNumber,
          AccountID: invoice.AccountID,
          AccountName: invoice.AccountName,
          ManufacturerID: invoice.ManufacturerID,
          ManufacturerName: invoice.ManufacturerName,
          OrderID: invoice.OrderID,
          PONumber: invoice.PONumber,
          OrderType: invoice.OrderType,
        },
        "addpaymentsapplied"
      );
      resetModal();
      await loadPayments(activeDateRange);
      await Swal.fire("Added!", "New Payment Added!", "success");
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Payment Submission Failed",
        text: getErrorMessage(error, "Please try again"),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const updatePayment = async () => {
    if (
      !canEditPayment ||
      !selectedPayment ||
      isReadOnlyPayment(selectedPayment) ||
      !validatePaymentForm({ requireInvoice: false })
    ) {
      return;
    }

    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You are about to update this payment.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: colors["successColor"],
      cancelButtonColor: colors["failureColor"],
      confirmButtonText: "Confirm",
    });
    if (!result.isConfirmed) return;

    setSubmitting(true);
    try {
      const apiService = new APIService();
      await apiService.addDataToBackend(
        {
          ID: selectedPayment.ID,
          InvoiceID: selectedPayment.InvoiceID,
          CheckNumber: formValues.checkNumber.trim(),
          AppliedTotal: Number(formValues.appliedTotal),
          PaymentType: formValues.paymentType,
        },
        "updatepaymentsapplied"
      );
      resetModal();
      await loadPayments(activeDateRange);
      await Swal.fire("Updated!", "Payment Updated!", "success");
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: getErrorMessage(error, "Please try again"),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const deletePayment = async (payment = selectedPayment) => {
    if (
      !canDeletePayment ||
      !payment ||
      isReadOnlyPayment(payment)
    ) {
      return;
    }

    const result = await Swal.fire({
      title: "Delete payment?",
      text: "This will remove the payment and recalculate the invoice balance. You cannot undo this action.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: colors["failureColor"],
      cancelButtonColor: colors["primaryColor"],
      confirmButtonText: "Delete",
    });
    if (!result.isConfirmed) return;

    setSubmitting(true);
    try {
      const apiService = new APIService();
      await apiService.addDataToBackend(
        { ID: payment.ID },
        "deletepaymentapplied"
      );
      resetModal();
      await loadPayments(activeDateRange);
      await Swal.fire("Deleted!", "Payment Deleted!", "success");
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text: getErrorMessage(error, "Please try again"),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSearch = (value) => {
    setSearchKeyword(value);
    setPaymentsAppliedData(applySearch(allPayments, value));
  };

  const handleDateFilterChange = async (dateRange) => {
    setActiveDateRange(dateRange);
    await loadPayments(dateRange);
  };

  const tableHeadersPaymentDashboard = [
    {
      headerName: "Check Number",
      field: "CheckNumber",
      filter: true,
    },
    {
      headerName: "Applied Date",
      field: "AppliedDate",
      filter: true,
    },
    {
      headerName: "Account Name",
      field: "AccountName",
      filter: true,
      cellRenderer: (params) => (
        <TableLink
          value={params.data.AccountName}
          path={`/Account/${params.data.AccountID}`}
        />
      ),
    },
    {
      headerName: "Invoice",
      field: "InvoiceNumber",
      filter: true,
      cellRenderer: (params) => (
        <TableLink
          value={params.data.InvoiceNumber}
          path={`/InvoiceHome/${params.data.InvoiceID}`}
        />
      ),
    },
    {
      headerName: "Payment Total",
      field: "PaymentAppliedDisplayTotal",
      filter: true,
      cellDataType: "number",
      valueGetter: (params) =>
        Number(
          String(params.data.PaymentAppliedDisplayTotal).replace(
            /[^0-9.-]+/g,
            ""
          )
        ),
      cellRenderer: (params) => params.data.PaymentAppliedDisplayTotal,
    },
    {
      headerName: "Action",
      field: "actions",
      sortable: false,
      filter: false,
      minWidth: canDeletePayment ? 210 : 120,
      cellRenderer: (params) => {
        const payment = params.data;
        const readOnly = isReadOnlyPayment(payment);
        const canEditRow = canEditPayment && !readOnly;
        const canDeleteRow = canDeletePayment && !readOnly;

        return (
          <TableOptionsCell
            optionOne={canEditRow ? "Edit" : "View"}
            optionOneType={canEditRow ? "warning" : "primary"}
            optionOneClickFn={() =>
              openPaymentModal(payment, canEditRow ? "edit" : "view")
            }
            optionTwo={canDeleteRow ? "Delete" : undefined}
            optionTwoType={canDeleteRow ? "failure" : undefined}
            optionTwoClickFn={
              canDeleteRow ? () => deletePayment(payment) : undefined
            }
            rowData={payment}
          />
        );
      },
    },
  ];

  const isViewMode = modalMode === "view";
  const isEditMode = modalMode === "edit";
  const isAddMode = modalMode === "add";
  const selectedInvoice = formValues.invoice;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
      <TitleBar
        label="Payments Applied"
        invoiceNumber={`${paymentsAppliedData.length}`}
        rightFilterBarComponents={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <Input.Search
              placeholder="Search"
              allowClear
              style={{ height: "36px" }}
              onSearch={handleSearch}
              onChange={(event) => {
                if (!event.target.value) handleSearch("");
              }}
            />
            {canAddPayment && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openAddModal}
                loading={submitting}
                style={{
                  background: colors["primaryColor"],
                  height: "36px",
                }}
              >
                Add Payment
              </Button>
            )}
          </div>
        }
      />

      <DateRangeFilter
        onFilterChange={handleDateFilterChange}
        labelText="Filter by date"
      />

      {loading ? (
        <Flex align="center" justify="center" style={{ minHeight: "240px" }}>
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
          />
        </Flex>
      ) : (
        <CommonTable
          rowData={paymentsAppliedData}
          columnDefs={tableHeadersPaymentDashboard}
        />
      )}

      <Modal
        open={Boolean(modalMode)}
        onCancel={resetModal}
        title={
          isAddMode
            ? "Add Payment"
            : isEditMode
              ? "Edit Payment"
              : "View Payment"
        }
        centered
        footer={null}
        destroyOnClose
      >
        <Row gutter={16} className="form-row">
          <Col span={24}>
            <label className="form-label">Invoice Number</label>
            {isAddMode ? (
              <Select
                aria-label="Invoice Number"
                showSearch
                optionFilterProp="label"
                placeholder="Select Invoice"
                value={selectedInvoice?.value}
                options={invoices}
                onChange={(_value, option) => {
                  updateFormValue("invoice", option);
                }}
                style={{ width: "100%" }}
              />
            ) : (
              <Input value={selectedPayment?.InvoiceNumber ?? ""} disabled />
            )}
          </Col>
        </Row>

        <Row gutter={16} className="form-row">
          <Col span={24}>
            <label className="form-label">PO Number</label>
            <Input
              value={
                isAddMode
                  ? selectedInvoice?.PONumber ?? ""
                  : selectedPayment?.PONumber ?? ""
              }
              disabled
            />
          </Col>
        </Row>

        <Row gutter={16} className="form-row">
          <Col span={24}>
            <label className="form-label">
              Transaction ID / Check Number
            </label>
            <Input
              aria-label="Transaction ID / Check Number"
              value={formValues.checkNumber}
              onChange={(event) =>
                updateFormValue("checkNumber", event.target.value)
              }
              placeholder="Enter Transaction ID / Check Number"
              disabled={isViewMode}
            />
          </Col>
        </Row>

        <Row gutter={16} className="form-row">
          <Col span={12}>
            <label className="form-label">Payment Amount</label>
            <Input
              aria-label="Payment Amount"
              type="number"
              min="0.01"
              step="0.01"
              value={formValues.appliedTotal}
              onChange={(event) =>
                updateFormValue("appliedTotal", event.target.value)
              }
              placeholder="Enter Amount"
              disabled={isViewMode}
            />
          </Col>
          <Col span={12}>
            <label className="form-label">Applied Date</label>
            <DatePicker
              aria-label="Applied Date"
              format="MM-DD-YYYY"
              value={formValues.appliedDate}
              onChange={(date) => updateFormValue("appliedDate", date)}
              disabled={!isAddMode}
              style={{ width: "100%" }}
            />
          </Col>
        </Row>

        <Row gutter={16} className="form-row">
          <Col span={24}>
            <label className="form-label">Payment Type</label>
            <Select
              aria-label="Payment Type"
              placeholder="Select Payment Type"
              value={formValues.paymentType}
              options={PAYMENT_TYPES}
              onChange={(value) => updateFormValue("paymentType", value)}
              disabled={isViewMode}
              style={{ width: "100%" }}
            />
          </Col>
        </Row>

        <Flex gap="small" style={{ marginTop: "20px" }}>
          {isAddMode && (
            <Button
              type="primary"
              onClick={addPayment}
              loading={submitting}
              style={{ background: colors["successColor"] }}
            >
              Add Payment
            </Button>
          )}
          {isEditMode && (
            <Button
              type="primary"
              onClick={updatePayment}
              loading={submitting}
              style={{ background: colors["successColor"] }}
            >
              Save
            </Button>
          )}
          {isEditMode &&
            canDeletePayment &&
            !isReadOnlyPayment(selectedPayment) && (
              <Button
                danger
                onClick={() => deletePayment(selectedPayment)}
                loading={submitting}
              >
                Delete
              </Button>
            )}
          {isViewMode && <Button onClick={resetModal}>Okay</Button>}
        </Flex>
      </Modal>
    </div>
  );
};

export default PaymentsDashboard;
